<script setup>
import { post } from "@/apis";
import { watchEffect, ref, nextTick } from "vue";
import { useRouter } from "vue-router";
import ArrowUp from "@/assets/arrow-up.png";
import ArrowGrey from "@/assets/arrow-grey.png";
import MenuIcon from "@/assets/menu-icon.png";
import IconResource from "@/assets/icon-resource.png";
import IconQuestion from "@/assets/icon-question.png";
import IconAudio from "@/assets/icon-audio.png";
import IconVideo from "@/assets/icon-video.png";
import IconPointReading from "@/assets/icon-point-reading.png";
import IconBookLock from "@/assets/icon-book-lock.png";
import IconDictation from "@/assets/icon-dictation.png";
import IconSchulte from "@/assets/icon-schulte.png";
import { openApp } from "@/utils/appLink";
import Empty from "@/components/Empty.vue";

import CustomDialog from "../../../components/CustomDialog.vue";

const typeMapIcon = {
  resource: IconResource,
  question: IconQuestion,
  audio: IconAudio,
  video: IconVideo,
  pointReading: IconPointReading,
  DICTATION: IconDictation,
  SCHULTE: IconSchulte,
};

const router = useRouter();

const { rank } = defineProps(["rank"]);

const showDialogTypes = [
  "question",
  "audio",
  "video",
  "pointReading",
  "DICTATION",
  "SCHULTE",
];
const menuTree = ref([]);
const activeNames = ref([]);
const clickItem = ref(null);
const showCustomDialog = ref(false);
const customDialogTitle = ref("");
const customDialogContent = ref("");

const getTree = async () => {
  const res = await post(
    "/api/classpal/app/v1/h5/app-books-rank-in-code-contents/app/books/rank/in/code/contents/tree",
    {
      rankClassifyId: rank.id,
    },
  );
  menuTree.value = res.data;
  res.data.forEach((item, index) => {
    if (item.children.length) {
      activeNames.value.push(item.id);
    }
    if (item.children.length) {
      item.children.forEach((el) => {
        if (el.children.length) {
          activeNames.value.push(el.id);
        }
      });
    }
  });
};

const handleClick = (item) => {
  if (item.isLock) {
    return false;
  }
  if (item.type === "resource") {
    router.push({
      path: "/material",
      query: {
        ...router.currentRoute.value.query,
        id: item.id,
        name: item.contentName,
        bookName: document.title,
        from: "booklet",
      },
    });
  }
  if (showDialogTypes.includes(item.type)) {
    clickItem.value = item;
    if (item.type === "question") {
      customDialogTitle.value = "答题练习";
      customDialogContent.value = "答题练习";
    }
    if (item.type === "audio") {
      customDialogTitle.value = "音频专辑";
      customDialogContent.value = "音频专辑";
    }
    if (item.type === "video") {
      customDialogTitle.value = "视频课程";
      customDialogContent.value = "视频课程";
    }
    if (item.type === "pointReading") {
      customDialogTitle.value = "点读内容";
      customDialogContent.value = "点读";
    }
    if (item.type === "DICTATION") {
      customDialogTitle.value = "字词听写";
      customDialogContent.value = "字词听写练习";
    }
    if (item.type === "SCHULTE") {
      customDialogTitle.value = "舒尔特方格";
      customDialogContent.value = "舒尔特方格练习";
    }
    showCustomDialog.value = true;
  }
};

const onConfirm = () => {
  if (clickItem.type === "video" || clickItem.type === "audio") {
    openApp({
      routePath:
        clickItem.type === "video" ? "videoAlbumPage" : "audioAlbumPage",
      h5Url: clickItem.h5PageUrl,
      id: clickItem.id,
      title: clickItem.contentName,
      businessType: clickItem.type == "video" ? 6 : 5,
    });
  } else {
    openApp({
      id: clickItem.id,
      title: clickItem.contentName,
    });
  }
};

watchEffect(() => {
  if (rank.id) {
    getTree();
  }
});
</script>

<template>
  <div v-if="menuTree.length === 0" style="width: 100%; height: 100%">
    <Empty />
  </div>
  <div class="menu-container" v-else>
    <div v-for="menu in menuTree" :key="menu.id" class="menu">
      <van-collapse v-model="activeNames">
        <van-collapse-item
          :name="menu.id"
          :border="false"
          :is-link="false"
          :readonly="menu.type !== 'directory'"
          class="menu-first"
          :class="menu.children.length ? 'menu-first-has-content' : ''"
        >
          <template #title>
            <div class="menu-first-title" @click="handleClick(menu)">
              <van-image
                v-if="menu.type === 'directory'"
                class="menu-img"
                :src="MenuIcon"
              />
              <van-image
                v-if="menu.type !== 'directory'"
                class="resource-icon"
                :src="typeMapIcon[menu.type]"
              />
              <div
                :class="[
                  'van-ellipsis',
                  'menu-title',
                  menu.type === 'directory' ? '' : 'menu-title-is-resource',
                ]"
              >
                {{ menu.contentName }}
              </div>
              <van-image
                v-if="menu.type !== 'directory' && menu.isLock"
                :src="IconBookLock"
                class="book-lock-icon"
              />
              <van-image
                v-if="menu.type === 'directory'"
                class="arrow-img"
                :src="ArrowUp"
                :style="{
                  transform: activeNames.includes(menu.id)
                    ? 'rotate(0deg)'
                    : 'rotate(180deg)',
                }"
              />
              <van-image v-else class="arrow-img-href" :src="ArrowGrey" />
            </div>
          </template>

          <div v-for="item in menu.children" :key="item.id" class="menu-item">
            <van-collapse v-model="activeNames">
              <van-collapse-item
                :name="item.id"
                :border="false"
                :readonly="!item.children.length"
                :is-link="item.type === 'directory' && item.children.length > 0"
                class="menu-second"
              >
                <template #title>
                  <van-image
                    v-if="item.type !== 'directory'"
                    class="resource-icon"
                    :src="typeMapIcon[item.type]"
                  />
                  <div
                    class="van-ellipsis menu-title"
                    :style="
                      activeNames.includes(item.id)
                        ? {
                            marginRight: '5px',
                          }
                        : {}
                    "
                    @click="handleClick(item)"
                  >
                    {{ item.contentName }}
                  </div>
                  <van-image
                    v-if="item.type !== 'directory' && item.isLock"
                    :src="IconBookLock"
                    class="book-lock-icon"
                  />
                </template>

                <template #right-icon>
                  <div style="display: flex; align-items: center">
                    <van-image
                      class="arrow-img-href"
                      :src="ArrowGrey"
                      :style="
                        activeNames.includes(item.id)
                          ? {
                              transform:
                                ' rotate(-90deg) translateX(-2px) translateY(-2px)',
                            }
                          : {}
                      "
                    />
                  </div>
                </template>

                <div v-for="el in item.children" :key="el.id" class="menu-item">
                  <van-collapse v-model="activeNames">
                    <van-collapse-item
                      :name="el.id"
                      :border="false"
                      :is-link="
                        el.type === 'directory' && el.children.length > 0
                      "
                      class="menu-third"
                    >
                      <template #title>
                        <div class="menu-third-title" @click="handleClick(el)">
                          <van-image
                            v-if="el.type !== 'directory'"
                            class="resource-icon"
                            :src="typeMapIcon[el.type]"
                          />
                          <div class="van-ellipsis menu-title">
                            {{ el.contentName }}
                          </div>
                          <van-image
                            v-if="el.type !== 'directory' && el.isLock"
                            :src="IconBookLock"
                            class="book-lock-icon"
                          />
                          <van-image
                            v-if="el.type !== 'directory'"
                            class="arrow-img-href"
                            :src="ArrowGrey"
                            :style="{
                              transform: activeNames.includes(el.id)
                                ? 'rotate(0deg)'
                                : '',
                            }"
                          />
                        </div>
                      </template>
                    </van-collapse-item>
                  </van-collapse>
                </div>
              </van-collapse-item>
            </van-collapse>
          </div>
        </van-collapse-item>
      </van-collapse>
    </div>
  </div>

  <CustomDialog
    v-model="showCustomDialog"
    :title="customDialogTitle"
    :content="customDialogContent"
    @confirm="onConfirm"
  />
</template>

<style lang="scss" scoped>
.menu-container {
  padding: 12px 16px;
}
.menu {
  background-color: #fff;
  border-radius: 12px;
  padding: 12px;
  font-size: 14px;
  color: #000000d9;
  &:not(:last-child) {
    margin-bottom: 12px;
  }
  :deep(.van-collapse) {
    &::after {
      display: none;
    }
    .van-cell {
      padding: 0;
    }
    .van-cell__title {
      width: 100%;
      min-width: 0;
      display: flex;
      align-items: center;
    }
    .van-collapse-item__content {
      padding: 0 0 0 16px;
    }
  }
}
.menu-first-has-content {
  & > :deep(.van-collapse-item__wrapper) {
    margin-top: 24px;
  }
  & > :deep(.van-collapse-item__wrapper) {
    .menu-item {
      &:not(:last-child) {
        margin-bottom: 24px;
      }
    }
  }
}
.menu-first {
  .menu-title {
    font-size: 16px;
    font-weight: 600;
    flex: 1;
    &.menu-title-is-resource {
      font-size: 14px;
      font-weight: normal;
    }
  }
  .menu-second {
    .menu-title {
      font-size: 14px;
      font-weight: normal;
    }
  }
}
.menu-first-title {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  .menu-img {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-right: 4px;
  }
  .arrow-img {
    width: 14px;
    height: 8px;
    flex-shrink: 0;
    margin-left: 18px;
    transition: transform 0.3s ease;
  }
}

.menu-second {
  & > :deep(.van-collapse-item__wrapper) {
    margin-top: 24px;
  }
}
.menu-third-title {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.arrow-img-href {
  width: 8px;
  height: 16px;
}
.resource-icon {
  width: 20px;
  height: 20px;
  margin-right: 4px;
}
.book-lock-icon {
  width: 24px;
  height: 24px;
  margin-right: 5px;
}
</style>
